import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';
import AbbrTip from '@/components/AbbrTip';

const FAQS = [
  {
    q: 'Is the NIIT calculated on total income or just investment income?',
    a: "You never pay NIIT on your W-2 wages or salary. The 3.8% surtax is only assessed on your Net Investment Income (dividends, capital gains, rental income, taxable bond interest, etc.). However, high active wages drag your total MAGI past the threshold line, making whatever investment income you DO have vulnerable to the tax. So wages indirectly cause NIIT, even though they're never directly taxed by it.",
  },
  {
    q: 'How do I report NIIT to the IRS?',
    a: 'Your tax software or accountant calculates it on IRS Form 8960 (Net Investment Income Tax) and attaches it directly to your primary Form 1040. The form walks through every category of investment income, applies the deductions allocable to that income, and produces the 3.8% surtax figure that flows to Schedule 2.',
  },
  {
    q: 'Do Traditional IRA / 401(k) distributions trigger NIIT?',
    a: "Distributions from Traditional IRAs and 401(k)s are NOT directly subject to NIIT — they're ordinary income, not investment income. BUT they DO count toward your MAGI, which is the line that determines whether your other investment income gets hit. A $50,000 Traditional IRA distribution could push your MAGI from $190,000 to $240,000, suddenly making your taxable-account dividends NIIT-eligible. This is why Roth conversions and Required Minimum Distributions (RMDs) need NIIT-aware planning, even though the distribution itself escapes the surtax.",
  },
  {
    q: 'Are qualified dividends still &quot;qualified&quot; if they get hit by the NIIT?',
    a: "Yes. The NIIT is an additional tax layered on top — it doesn't change a dividend's classification. A qualified dividend in the 15% bracket plus the 3.8% NIIT pays a total of 18.8%, but it's still classified as a qualified dividend on your return. The same dividend held inside a Roth IRA pays 0% federal tax (both ordinary AND NIIT) because Roth qualified distributions are fully exempt.",
  },
  {
    q: 'Are the NIIT thresholds going to be raised soon?',
    a: 'Almost certainly not. The thresholds ($200K single / $250K MFJ / $125K MFS) have been frozen since the tax went into effect in 2013 — they are deliberately NOT indexed for inflation. Every Congress since has had opportunities to raise them; none has. That means as wages and portfolio sizes naturally grow with inflation, more investors cross the line each year. Plan as if the thresholds will stay exactly where they are.',
  },
];

export default function LearnNiitSurtax() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a.replace(/&quot;/g, '"') },
    })),
  };

  return (
    <LearnPage
      title="Navigating the Net Investment Income Tax (NIIT) — 3.8% Surtax Guide (2026) | DiviDrip"
      description="The 3.8% NIIT surtax explained: 2026 thresholds, what's hit vs exempt, how to reduce exposure, and worked examples for dividend investors."
      canonical="https://twylightcrow.com/learn/niit-surtax"
      guidePath="/learn/niit-surtax"
      sectionLabel="Dividend Taxes"
      sectionAnchor="tax"
      kicker="Learn · Dividend taxes"
      h1="Navigating the Net Investment Income Tax (NIIT) — The 3.8% Surtax Guide"
      datePublished="2026-05-26"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — 2026 tax guide' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP in your Roth, not your taxable' },
        { to: '/learn/tax-loss-harvesting-playbook', label: 'The Tax-Loss Harvesting Playbook for dividend investors' },
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers to check before any buy' },
        { to: '/learn/dividend-traps', label: 'Spot a dividend trap before it cuts' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs, BDCs, and CEFs — high-yield tax treatment' },
        { to: '/learn/covered-call-etfs', label: 'Covered call ETFs and their ordinary income tax bite' },
        { to: '/screener', label: 'Stock Screener — filter by yield, sector, frequency' },
      ]}
    >
      <p>
        Once your dividend portfolio reaches a meaningful size, or your career income
        climbs, a stealthy tax line kicks in: the{' '}
        <strong>Net Investment Income Tax (<AbbrTip term="NIIT">NIIT</AbbrTip>)</strong>. Enacted under the Affordable
        Care Act and in effect since 2013, this <strong>3.8% surtax</strong> sits on top of
        your regular capital gains and dividend obligations. The real kicker? Unlike
        regular tax brackets, its income thresholds are <em>not</em> indexed for
        inflation — capturing more dividend investors every single year.
      </p>

      <h2>The NIIT thresholds (2026)</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Filing status</th>
            <th className="text-left font-semibold"><AbbrTip term="MAGI">MAGI</AbbrTip> threshold</th>
            <th className="text-left font-semibold">Surtax rate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Single / Head of Household</td>
            <td>$200,000</td>
            <td>3.8%</td>
          </tr>
          <tr>
            <td className="font-semibold">Married Filing Jointly / Qualifying Widow(er)</td>
            <td>$250,000</td>
            <td>3.8%</td>
          </tr>
          <tr>
            <td className="font-semibold">Married Filing Separately</td>
            <td>$125,000</td>
            <td>3.8%</td>
          </tr>
        </tbody>
      </table>
      <p>
        Thresholds are based on <strong>Modified Adjusted Gross Income (MAGI)</strong>. The
        3.8% tax applies to the <em>lesser</em> of two numbers: your total net investment
        income, or the dollar amount by which your MAGI exceeds the threshold line. That
        &quot;lesser of&quot; structure is the key — if you cross the threshold by only
        $5,000 but have $50,000 of dividends, you only pay NIIT on the $5,000.
      </p>

      <h2>What gets hit (and what escapes)</h2>
      <p>The IRS draws a sharp boundary around what qualifies as Net Investment Income:</p>
      <p>
        <strong>Subject to NIIT:</strong> taxable brokerage dividends (both qualified and
        ordinary), realized short-term and long-term capital gains, rental income, royalties,
        non-qualified annuities, and taxable bond interest.
      </p>
      <p>
        <strong>Exempt from NIIT:</strong> qualified distributions from tax-advantaged
        accounts (Traditional IRAs, Roth IRAs, 401(k)s, HSAs), active business wages,
        Social Security, alimony, unemployment, and tax-exempt municipal bond interest.
      </p>
      <p className="text-xs text-muted-foreground italic">
        Subtle but important: a Traditional IRA / 401(k) distribution itself isn&apos;t
        taxed by NIIT, but it <em>does</em> raise your MAGI — which can push your other
        investment income over the threshold. Roth qualified distributions don&apos;t
        even count toward MAGI, which is one reason they&apos;re so powerful at scale.
      </p>

      <h2>The compounding bracket damage</h2>
      <p>
        For middle-to-high income earners, the NIIT effectively reshapes the dividend
        tax brackets. If your ordinary wages fill the $200,000 threshold, every dollar of
        dividend income you earn in a taxable account gets dragged upward:
      </p>
      <ul>
        <li>
          The <strong>15% qualified dividend rate</strong> effectively shifts to{' '}
          <strong>18.8%</strong>.
        </li>
        <li>
          The <strong>20% qualified dividend rate</strong> effectively shifts to{' '}
          <strong>23.8%</strong>.
        </li>
        <li>
          Ordinary dividend rates (from REITs, BDCs, or covered-call ETFs) can peak at{' '}
          <strong>40.8%</strong> (37% top ordinary bracket + 3.8% NIIT).
        </li>
      </ul>

      <h2>The high-earner reduction playbook</h2>
      <p>
        If you&apos;re crossing these lines in 2026, you have three primary levers to
        reduce your NIIT exposure:
      </p>
      <ol>
        <li>
          <strong>Max out pre-tax accounts.</strong> Maximising Traditional 401(k) and HSA
          contributions lowers your baseline MAGI, which can pull your total income back
          below the $200,000 / $250,000 threshold walls. Every dollar contributed pre-tax
          reduces MAGI dollar-for-dollar.
        </li>
        <li>
          <strong>Prioritise tax-exempt vehicles inside taxable accounts.</strong> Swap
          cash allocations or taxable bond ETFs for municipal bond ETFs like{' '}
          <strong>MUB</strong> (iShares National Muni Bond) or <strong>VTEB</strong>{' '}
          (Vanguard Tax-Exempt Bond) in your taxable brokerage. Muni interest drops
          completely out of the NIIT equation — it doesn&apos;t count as Net Investment
          Income at all.
        </li>
        <li>
          <strong>Aggressive asset location.</strong> Shift high-yielding ordinary-income
          assets (BDCs, mortgage REITs, covered-call ETFs) <em>out</em> of your taxable
          accounts entirely. Inside a Roth or Traditional IRA, those distributions are
          fully exempt from NIIT (and from the underlying ordinary-income tax in a Roth).
        </li>
      </ol>

      <h2>The 2026 math in action</h2>
      <p>
        Imagine a married couple with a $280,000 MAGI made up of $250,000 in W-2 salaries
        and $30,000 from a taxable dividend portfolio:
      </p>
      <ul>
        <li>
          They exceed the joint threshold by exactly $30,000 ($280,000 − $250,000).
        </li>
        <li>Their Net Investment Income is also $30,000.</li>
        <li>
          The NIIT bill is the <em>lesser</em> of the two × 3.8% = $30,000 × 3.8% ={' '}
          <strong>$1,140</strong> in pure surtax — on top of the standard 15% qualified
          dividend tax (~$4,500) for a blended federal hit of ~18.8% on those dividends.
        </li>
      </ul>
      <p>
        If they&apos;d held those same dividend-paying stocks in a Roth IRA instead, both
        the qualified-dividend tax AND the NIIT would have been zero — a difference of
        roughly $5,640 per year that compounds across the rest of their working life.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt
              className="font-semibold text-foreground"
              dangerouslySetInnerHTML={{ __html: q }}
            />
            <dd
              className="mt-1 text-foreground/85"
              dangerouslySetInnerHTML={{ __html: a }}
            />
          </div>
        ))}
      </dl>

      <p>
        Once you understand which holdings trigger the NIIT bite, the next question is{' '}
        <em>where</em> to put them. The companion guide on{' '}
        <Link to="/learn/why-drip-roth">why DRIP in your Roth, not your taxable</Link>{' '}
        works through the account-placement decision in detail.
      </p>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: This is not tax advice. The NIIT interacts with state taxes,
        passive-activity rules, and your specific deductions in ways that need a CPA or
        Enrolled Agent. Use this guide to know what questions to ask — then bring it to a
        professional before making any major decision.
      </p>
    </LearnPage>
  );
}
